import { useCallback, useEffect } from 'react';

import router from 'next/router';
import { useAccount } from 'wagmi';

import BorrowLendLayout from '@/components/dashboard/borrow-land/BorrowLendLayout';
import BorrowLoan from '@/components/dashboard/borrow-land/BorrowLoan';
import ConnectionProvider from '@/components/dashboard/portfolio/ConnectionProvider';
import { Loader } from '@/components/Loader';
import { useAppDispatch } from '@/hooks/redux';
import { fetchNFTs } from '@/redux/loans/loansSlice';

const filterKeys = {
  name: {
    ASC: 'transfer_time__asc',
    DESC: 'transfer_time__desc',
  },
  floor: {
    ASC: 'floor_price__asc',
    DESC: 'floor_price__desc',
  },
};

type SortKeys = 'name' | 'floor';
type SortDir = 'ASC' | 'DESC';

const BorrowLoanPage = () => {
  const { isConnected, address, isConnecting } = useAccount();
  const dispatch = useAppDispatch();
  const sort = ((Array.isArray(router.query.sort) ? router.query.sort[0] : router.query.sort) as SortKeys) || 'floor';
  const dir = ((Array.isArray(router.query.dir) ? router.query.dir[0] : router.query.dir) as SortDir) || 'DESC';
  const { searchTerm, currency } = router.query;

  const fetchData = useCallback(() => {
    if (!address) return;
    const orderBy = filterKeys[sort]?.[dir] || filterKeys.floor.ASC;
    dispatch(fetchNFTs({ walletAddress: address, order_by: orderBy, collectionSearch: searchTerm, currency, includeRarity: true }));
  }, [address, dispatch, sort, dir, searchTerm, currency]);

  useEffect(() => {
    if (!filterKeys[sort] || !filterKeys[sort][dir]) {
      router.replace({
        pathname: router.pathname,
        query: { sort: 'name', dir: 'ASC' },
      });
      return;
    }

    fetchData();
  }, [sort, dir, fetchData, searchTerm, currency]);

  return (
    <BorrowLendLayout
      title='Get a Loan'
      description='Select an NFT or Real-World Asset as collateral and set your loan terms. NettyWorth will notify you of loan offers. Once active, your asset is securely held in escrow until the loan is due. Upon repayment, the asset is returned to your wallet; if defaulted, it is transferred to the lender.'
      extraDropdowns={['Currency', 'Asset']}
      showQuickLoanButton
      hideCurrency
    >
      {isConnecting ? <Loader /> : !isConnected ? <ConnectionProvider connectWalletText='Connect your wallet to borrow.' /> : <BorrowLoan />}
    </BorrowLendLayout>
  );
};

BorrowLoanPage.signedInDesign = true;

BorrowLoanPage.metadata = {
  title: 'NettyWorth | Borrow Against Your Crypto, NFTs and Gaming Assets',
  description: 'Unlock liquidity from your Crypto, NFTs, and Gaming Assets without selling them. Borrow securely on NettyWorth’s loan marketplace.',
  image: `${process.env.NEXT_PUBLIC_SITE_URL}/thumbnail.png`,
  contentType: 'product',
};

export default BorrowLoanPage;
